import React, { useEffect, useState, useContext, useMemo } from 'react';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Redirect } from 'react-router-dom';
import { transformToDataApiFormat } from '../components/Prompt/Content/VersionCommentContent';
import PromptContext from '../components/Prompt/PromptContext';
import { getLanguage, getAvailableLanguages } from '../features/app/selectors';
import { fetchPpi } from '../features/PpiContainer/actions';
import getPublishSteps from '../features/PpiContainer/publishSteps/publishSteps';
import { MARKET_GLOBAL_LANG } from '../features/app/constants';

import useGlobalPpis from '../features/PpiContainer/hooks/useGlobalPpis';
import globalPpiImport from '../features/PpiContainer/globalPpiImport';
import {
  getPpiById,
  getIsFetching,
  getFetchError
} from '../features/PpiContainer/selectors';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import PpiForm from '../components/PpiForm';
// import {
//   PREPARATION_TIME_UNIT_DEFAULT,
//   PRODUCTION_TIME_UNIT_DEFAULT,
//   SECOND_SHELF_LIFE_UNIT_DEFAULT,
//   SERVING_HOLDING_TEMP_UNIT_DEFAULT,
//   SERVING_HOLDING_TIME_UNIT_DEFAULT
// } from '../features/PpiContainer/constants';
import useSavePpi from '../features/PpiContainer/hooks/useSavePpi';
import {
  usePublishPpi,
  useImportUpdatePpi,
  useImportGlobalPpiUpdate,
  useDiscardDraftPpi
} from '../features/PpiContainer/hooks/usePpiAction';
import { States } from '../features/PpsiContainer/constants';
import { getPpsisWithFilter } from '../features/PpsiContainer/selectors';

import { toJS } from '../utils/immutableUtils';
import Splash from '../components/Splash';
import { ppiPublishedRoute, draftPPIsRoute } from '../routes';
import { Mode } from '../components/PpiForm/constants';
import usePpsis from '../features/PpsiContainer/hooks/usePpsis';
import View from './views';
import useAlert from '../features/alertSystem/hooks/useAlert';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import DiscardDraftConfirmation from './DiscardDraftConfirmation';

// Hard-coded for now, could potentially be passed and query string parameter
const copyFromLanguage = MARKET_GLOBAL_LANG;

function getData(ppi) {
  let data = _.cloneDeep(ppi.inProgressPPIEnrichmentData);
  data = {
    ...data,
    ppiId: ppi.id,
    itemCode: ppi.itemCode,
    relationshipId: ppi.relationshipId
    // Added during development to "self-heal" PPIs without the default units.
    // This can be removed down the road when all PPIs are created from scratch
    // at which point the units are populated with default units.
    // preparationTimeUnit: PREPARATION_TIME_UNIT_DEFAULT,
    // secondShelfLifeUnit: SECOND_SHELF_LIFE_UNIT_DEFAULT,
    // productionTimeUnit: PRODUCTION_TIME_UNIT_DEFAULT,
    // servingHoldingTempUnit: SERVING_HOLDING_TEMP_UNIT_DEFAULT,
    // servingHoldingTimeUnit: SERVING_HOLDING_TIME_UNIT_DEFAULT
  };
  return data;
}

function mapToSaveData(formData) {
  const id = formData.ppiId;
  const data = _.omit(formData, 'itemCode', 'ppiId');
  return { id, data };
}

const PpiEditView = () => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isFetchingGlobal, globalPpisRaw] = useGlobalPpis({
    status: States.PUBLISHED
  });
  const dispatch = useDispatch();
  const [notStartedFetching, setNotStartedFetching] = useState(true);
  const [discardDraftPpi, isdiscardingDraft] = useDiscardDraftPpi();

  const { id } = useParams();
  // TODO Use usePpi() and forceFetch to ensure latest data is available in form
  const ppi = toJS(useSelector(getPpiById(id)));
  const languages = useSelector(getAvailableLanguages);
  const isFetchingPpi = useSelector(getIsFetching);
  const fetchError = useSelector(getFetchError);
  const activeLanguage = useSelector(getLanguage);
  const isFetching = notStartedFetching || isFetchingPpi;

  const getPpiRelationshipId = ppi && ppi.relationshipId;

  const [savePpi, isSaving] = useSavePpi();
  const [publishPpi, isPublishing, isPublished] = usePublishPpi();
  const [importUpdatePpi, isUpdating] = useImportUpdatePpi();
  const [
    importGlobalUpdatePpi,
    getIsImportingGlobalPpi
  ] = useImportGlobalPpiUpdate();
  const [popUpDisplayed, setPopUpdisplayed] = useState(false);
  // Fetching all used in PPSIs as it required it tab contents
  const [isFetchingUsedInPpsis] = usePpsis({
    relatedToPpiId: id
  });
  const filter = useMemo(
    () => ({ relatedToPpiId: id, status: States.PUBLISHED }),
    [id]
  );
  const affectedPpsis = useSelector(getPpsisWithFilter(filter));

  // GET ALL GLOBAL PPIS TO IMPORT DATA FROM
  const globalPpis = toJS(globalPpisRaw, []);

  // FILTER GLOBAL PPSIS WITH PIA RELATIONSHIP ID
  // const getGlobalPpitoImport =
  //   globalPpis &&
  //   globalPpis.filter(i => i.relationshipId === getPpiRelationshipId);

  // REMOVE MULTIPLE GLOBAL INSTRUCTION WITH SINGLE PIA ARTICLE NUMBER
  //const finalGlobalPpitoImport = getGlobalPpitoImport[0];
  const [openAlert, closeAlert] = useAlert();

  // IMPORT AND MERGE GLOBAL UPDATE DATA
  function getGlobalImportData(datas, ppi) {
    const data = {
      ...datas,
      ppiId: ppi.ppiId,
      id: ppi.id,
      referencedIngredientData: ppi.referencedIngredientData,
      itemCode: ppi.itemCode
    };
    return data;
  }

  const { open } = useContext(PromptContext);

  // if(ppi !== undefined && ppi !== null && isFetching === false && isFetchingUsedInPpsis === false && popUpDisplayed === false){
  //   if(ppi.inProgressPPIEnrichmentData !== null){
  //     if(ppi.inProgressPPIEnrichmentData.upToDate === false){
  //       setTimeout(()=>{
  //        alert("Update available");
  //       },1000)
  //       setPopUpdisplayed(true)
  //     }
  //   }
  // }

  useEffect(() => {
    dispatch(fetchPpi(id));
    setNotStartedFetching(false);
  }, [id, dispatch]);

  function handleSavePpi(formData, callback) {
    const { id: ppiId, data } = mapToSaveData(formData);
    savePpi(ppiId, data, callback);
  }

  function handlePublishPpi(ppiData) {
    const steps = getPublishSteps(ppiData, affectedPpsis);
    const { ppiId } = ppiData;

    const onDone = ({ promptState }) => {
      const approvalData = transformToDataApiFormat(promptState);
      let body;
      if (approvalData) {
        body = {
          versionComments: approvalData,
          isUpdateSelected: approvalData.isUpdateSelected
        };
        delete body.versionComments.isUpdateSelected;
      } else {
        body = approvalData;
      }
      publishPpi(ppiId, body);
    };
    if (languages.size > 1) {
      openAlert({
        title: (
          <FormattedMessage id="notify.PpsiMultipleLanguageAlertMessage" />
        ),
        content: (
          <FormattedMessage id="notify.PpsiMultipleLanguageAlertMessageDesc" />
        ),
        actions: {
          right: [
            <Button
              key="saveAndLeave"
              onClick={() => {
                closeAlert();
              }}
              color="primary"
            >
              <FormattedMessage id="notify.PpsiMultipleLanguageAlertCancel" />
            </Button>,
            <Button
              key="stay"
              onClick={() => {
                closeAlert();
                if (steps.length > 0) {
                  open({ steps, data: ppiData, onDone });
                } else {
                  publishPpi(ppiId);
                }
              }}
              color="primary"
            >
              <FormattedMessage id="notify.PpsiMultipleLanguageAlertSuccess" />
            </Button>
          ]
        }
      });
    } else if (steps.length > 0) {
      open({ steps, data: ppiData, onDone });
    } else {
      publishPpi(ppiId);
    }
  }

  function importIfoodUpdate(ppiData) {
    importUpdatePpi(id);
  }

  if (fetchError) {
    throw fetchError;
  }
  if (isFetching || isFetchingUsedInPpsis || isFetchingGlobal) {
    return <ProgressFullscreen open />;
  }

  if (!ppi) {
    return <Redirect to={draftPPIsRoute()} />;
  }
  if (isPublished) {
    return <Redirect to={ppiPublishedRoute(id)} />;
  }

  // PPI has published / approved directly to published
  if (!ppi.inProgressPPIEnrichmentData && ppi.publishedPPIEnrichmentData) {
    return <Redirect to={ppiPublishedRoute(id)} />;
  }

  // if (!ppi.inProgressPPIEnrichmentData) {
  //   // TODO Add button which takes user to publish PPI
  //   return (
  //     <Splash content="Trying to edit PPI which has no draft version. Illegal state." />
  //   );
  // }

  View.PpiEdit.visit(ppi, activeLanguage);

  // Get the local market presentation name and append to Global import data
  function getLocalPresentationName(data, ppi) {
    const localPresentationName = ppi.info[activeLanguage].presentationName;
    const dataWithoutName = _.cloneDeep(data);
    dataWithoutName.info[
      activeLanguage
    ].presentationName = localPresentationName;
    return dataWithoutName;
  }

  function handleGlobalUpdatePpi() {
    importGlobalUpdatePpi(id);
    // if (finalGlobalPpitoImport === undefined) {
    //   return console.log('No Global PPI published EnrichmentData');
    // } else {
    //   let importGlobalData = globalPpiImport(
    //     finalGlobalPpitoImport,
    //     finalGlobalPpitoImport.publishedPPIEnrichmentData.id,
    //     copyFromLanguage,
    //     languages
    //   );

    //   let finalData = getLocalPresentationName(importGlobalData, ppi);
    //   const results = getGlobalImportData(finalData, ppi);
    //   const { id: ppiId, data } = mapToSaveData(results);
    //   savePpi(ppiId, data, callback);
    // savePpsi(results, callback);
    // }
  }

  const typePpi = ppi && ppi.hasOwnProperty('ppiVersion');

  function onPpiDiscardDraft() {
    discardDraftPpi(id);
  }

  return (
    <>
      <PpiForm
        data={getData(ppi)}
        relationshipId={ppi && ppi.relationshipId}
        showProgress={isSaving || isPublishing || getIsImportingGlobalPpi}
        dataLabels={{
          title: (data, { language }) =>
            _.get(data, `info.${language}.presentationName`),
          status: data => data.enrichmentStatus,
          modifiedBy: 'lastModifiedBy',
          modifiedDate: 'modifyDate'
        }}
        mode={Mode.EDIT}
        onSave={handleSavePpi}
        onPublish={handlePublishPpi}
        oniFoodUpdatePpi={importIfoodUpdate}
        onGlobalUpdatePpi={handleGlobalUpdatePpi}
        setOpenConfirm={setOpenConfirm}
        globalPpis={globalPpis}
      />

      {openConfirm && (
        <DiscardDraftConfirmation
          open={openConfirm}
          setOpen={setOpenConfirm}
          onPpiDiscardDraft={onPpiDiscardDraft}
          typePpi={typePpi}
        />
      )}
    </>
  );
};

export default PpiEditView;
